import { log } from '@execonline-inc/logging';
import { Maybe } from 'maybeasy';

export type IsNewerTurn = (turnUUID: string, currentTurnUUID: string | null) => boolean;

/**
 * Puts streamed ActionCable messages back in the order the server sent them.
 *
 * ActionCable delivers each message through a multi-threaded worker pool, so a
 * burst of broadcasts can reach the browser out of order. The server numbers
 * every message in a turn with a monotonic `seq`, counted from 0 per channel
 * and per turn. `accept` returns the messages that are ready to apply, in
 * sequence order, and holds back any message that arrives early.
 *
 * A message with no `seq` (sent by a server that does not number messages yet)
 * is returned at once, in arrival order.
 */
export class StreamSequencer<T> {
  private currentTurnUUID: string | null = null;
  private nextSeq = 0;
  private pending = new Map<number, T>();

  constructor(private readonly isNewerTurn: IsNewerTurn) {}

  reset = (): void => {
    this.currentTurnUUID = null;
    this.nextSeq = 0;
    this.pending = new Map();
  };

  accept = (turnUUID: string, seq: Maybe<number>, message: T): T[] => {
    if (!this.startTurnIfNewer(turnUUID)) {
      log('Skipping message - older turn', { turnUUID, currentTurnUUID: this.currentTurnUUID });
      return [];
    }

    return seq.map((seq) => this.acceptInSequence(seq, message)).getOrElseValue([message]);
  };

  private startTurnIfNewer = (turnUUID: string): boolean => {
    if (turnUUID === this.currentTurnUUID) return true;
    if (!this.isNewerTurn(turnUUID, this.currentTurnUUID)) return false;

    this.reset();
    this.currentTurnUUID = turnUUID;
    return true;
  };

  private acceptInSequence = (seq: number, message: T): T[] => {
    if (seq < this.nextSeq) {
      log('Skipping message - duplicate seq', { seq, nextSeq: this.nextSeq });
      return [];
    }

    this.pending.set(seq, message);

    const ready: T[] = [];
    let next = this.pending.get(this.nextSeq);
    while (next !== undefined) {
      ready.push(next);
      this.pending.delete(this.nextSeq);
      this.nextSeq += 1;
      next = this.pending.get(this.nextSeq);
    }
    return ready;
  };
}
